import { EntityTypes, ModalTypes } from '@dtx-company/inter-app/src/redux/slices/modal'
import { IthacaFilter } from '@dtx-company/flow-codegen/src/page/generated.types'
import { StorageVisualizerComponentProps } from '../../NavigationMenuV3/types'
import { UseProfileMenuStateType } from '../types'
import { getEntityAvatarName } from '@dtx-company/inter-app/src/components/org/utils/getEntityAvatarName'
import { getPlanTypeName } from '@app/common/src/utils/getPlanTypeName'
import {
  setMoreOptionsSheet,
  setProfileMenuSheetOpen
} from '@dtx-company/inter-app/src/redux/slices/config-slice'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useBillingPlanType } from '@app/common/src/hooks/useBillingPlanType/useBillingPlanType'
import { useDispatch } from 'react-redux'
import { useFlowpagePermissionsEnabled } from '@dtx-company/inter-app/src/hooks/useFlowpagePermissionsEnabled'
import { useGetUserInfo } from '@app/code/src/hooks/swr'
import { useModal } from '@dtx-company/inter-app/src/redux/slices/utils'
import { usePageCount } from '@dtx-company/inter-app/src/redux/slices/utils/pageCount'
import { useProfilePicture } from '@app/common/src/hooks/useProfilePicture'
import { useProfilePictureModal } from '@dtx-company/inter-app/src/hooks/useProfilePictureModal'
import { useSeatCount } from '@app/common/src/hooks/useSeatCount'
import React from 'react'
import events from '@dtx-company/inter-app/src/event-tracking/events/nav'

export const useProfileMenuState = (args?: { isDesktop?: boolean }): UseProfileMenuStateType => {
  const { jwt, hasOperation } = useAuthState()
  const dispatch = useDispatch()
  const profilePicture = useProfilePicture({
    entityId: jwt?.ithacaId,
    entityType: EntityTypes.USER,
    token: jwt?.token
  })

  const fullName = getEntityAvatarName({
    firstName: jwt?.firstName,
    lastName: jwt?.lastName,
    email: jwt?.email
  })
  const email = jwt?.email
  const orgName = jwt?.org?.name
  const planBillingType = useBillingPlanType(true)
  const planLabel = planBillingType ? getPlanTypeName(planBillingType) : undefined
  const { data: userInfoData } = useGetUserInfo()
  const codeCount = userInfoData?.me?.flowcodeCount
  const { codeLimit, pageLimit } = useAuthState()
  const sharePermissionEnabled = useFlowpagePermissionsEnabled()
  const pageCount = usePageCount(sharePermissionEnabled ? IthacaFilter.All : IthacaFilter.Owned)
  const { seatCount, userCount: memberCount } = useSeatCount()
  const progressBarConfig: StorageVisualizerComponentProps = {
    codeCount,
    codeLimit,
    pageLimit,
    pageCount,
    seatCount,
    memberCount,
    showSeatCount: Boolean(jwt?.org) && Boolean(seatCount) && seatCount !== -1
  }

  const closeProfileSheet = React.useCallback(
    () => dispatch(setProfileMenuSheetOpen({ open: false })),
    [dispatch]
  )
  const closeMobileMenus = React.useCallback(() => {
    closeProfileSheet()
    dispatch(setMoreOptionsSheet({ open: false }))
  }, [dispatch, closeProfileSheet])

  const { setClose: closeDesktopDialog } = useModal(ModalTypes.DESKTOP_PROFILE_MENU_MODAL)

  const showReferAFriendOption = planLabel?.toUpperCase() !== 'ENTERPRISE'

  // profile picture editing
  const { setOpen } = useProfilePictureModal()
  const onClickEditProfilePicture = (): void => {
    setOpen({
      entityId: jwt?.ithacaId || '',
      entityType: EntityTypes.USER,
      profilePicture: profilePicture || '',
      entityName: fullName
    })
  }

  //upgrade
  const userHasOperations = {
    viewProUpsellModal: hasOperation('view:pro_upsell_or_upgrade'), // Free user
    viewProPlusUpsellModal: hasOperation('view:pro_plus_upsell_modal') // Pro user
  }

  const showUpgradeButton =
    userHasOperations.viewProUpsellModal || userHasOperations.viewProPlusUpsellModal
  const { setOpen: openUpgradeDialog } = useModal(ModalTypes.NAV_PLANS_UPGRADE_DIALOG)
  const onClickUpgradeButton = (): void => {
    openUpgradeDialog()
    if (args?.isDesktop) {
      events.Platform_Desktop_TopNav_Clicked_Upgrade()
    } else {
      events.Platform_MobileMoreOptionsSheet_ProfileMenu_Clicked_Upgrade()
    }
  }

  return {
    orgName,
    planLabel,
    profilePicture,
    fullName,
    email,
    closeAllMenus: args?.isDesktop ? closeDesktopDialog : closeMobileMenus,
    showReferAFriendOption,
    progressBarConfig,
    onClickEditProfilePicture,
    showUpgradeButton,
    onClickUpgradeButton
  }
}
